import {
  BaseProps,
  Col,
  CommonStyles,
  Flex,
  Image,
  Row,
  Shape,
  SubTitle,
  ThemeProps,
  styled,
  useTheme,
} from '@teko/ds-desktop';
import { DownOutlined, UpOutlined } from '@teko/ds-icon';
import { Policy } from '@teko/ecommerce/mobile';
import DOMPurify from 'dompurify';
import { t } from 'i18n';
import React, { ReactElement, useState } from 'react';

export interface Information extends Omit<Policy, 'icon' | 'id'> {
  icon: ReactElement | string;
}

export interface PolicyAndServiceProps extends BaseProps {
  title: string;
  information: Information[];
  max?: number;
  columns?: number;
  showText?: string;
  hideText?: string;
}

export const PolicyAndService: React.FC<PolicyAndServiceProps> = ({
  title,
  information,
  max = 4,
  columns = 2,
  showText = t('Xem thêm'),
  hideText = t('Rút gọn'),
  ...rest
}: PolicyAndServiceProps) => {
  const theme = useTheme();
  const [maxItems, setMaxItems] = useState(max);

  // map data based on max items allow to display
  const displayInformation =
    maxItems < information.length
      ? information.slice(0, maxItems)
      : information;
  // calculate number of rows to display
  const numberOfRows = Math.round(displayInformation.length / columns);
  const minRows = Math.round(max / columns);

  // split display data into arrays size of display columns
  const displayItems = [];
  for (let index = 0; index < numberOfRows; index++) {
    displayItems.push(
      displayInformation.slice(index * columns, (index + 1) * columns),
    );
  }

  return (
    <Wrapper {...rest}>
      <SubTitle
        level={2}
        weight="medium"
        marginBottom="s12"
        style={{
          fontSize: '15px',
          lineHeight: '24px',
          color: 'rgba(0, 0, 0, 0.88)',
        }}
      >
        {title}
      </SubTitle>
      <Shape paddingBottom="s8">
        {displayItems.map((displayItem, displayItemIndex) => {
          return (
            <Row
              key={displayItemIndex}
              marginBottom={
                displayItemIndex < displayItems.length - 1 ? 's8' : undefined
              }
              justify="space-between"
              style={{ gap: '20px' }}
              className="test-row"
            >
              {displayItem.map((item, itemIndex) => {
                return (
                  <Col
                    key={itemIndex}
                    width={
                      displayItems.length === 1 ? '100%' : 'calc(50% - 10px)'
                    }
                  >
                    <PolicyItem>
                      {typeof item.icon === 'string' ? (
                        <StyledIcon width={24} height={24} src={item.icon} />
                      ) : (
                        item.icon
                      )}
                      <Content
                        dangerouslySetInnerHTML={{
                          __html: DOMPurify.sanitize(item.content || '', {
                            ALLOWED_ATTR: ['target', 'href'],
                          }),
                        }}
                      />
                    </PolicyItem>
                  </Col>
                );
              })}
            </Row>
          );
        })}
      </Shape>
      {maxItems < information.length || numberOfRows > minRows ? (
        <Flex justify="center" marginTop="s8">
          {maxItems < information.length ? (
            <StyledFlexWrapper
              align="center"
              gap="s4"
              onClick={() => {
                setMaxItems(information.length);
              }}
            >
              {showText}
              <DownOutlined size={16} color={theme.colors.primary} />
            </StyledFlexWrapper>
          ) : (
            <StyledFlexWrapper
              align="center"
              gap="s4"
              onClick={() => {
                setMaxItems(max);
              }}
            >
              {hideText}
              <UpOutlined size={16} color={theme.colors.primary} />
            </StyledFlexWrapper>
          )}
        </Flex>
      ) : null}
    </Wrapper>
  );
};

const Wrapper = styled.div`
  ${CommonStyles};
  background-color: #fff;
  border-radius: 0.5rem;
`;

const PolicyItem = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 8px;
`;

const Content = styled.div`
  & > * {
    margin: 0;
  }
  line-height: 22px;
  font-size: 14px;
`;

const StyledIcon = styled(Image)`
  min-width: 24px;
`;

const StyledFlexWrapper = styled(Flex)(
  ({ theme }: ThemeProps) => `
  cursor: pointer;
  background-color: unset;
  color: ${theme?.colors.primary};
  font-size: 13px;
  border: 0px;
  &:focus {
    border: 0px;
    outline: 0px;
  }
`,
);
